import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ExportDebugData from './ExportDebugData';

interface Sr89DebugFormData {
  calculationId: string;
}

export interface Sr89DebugResults {
  // Eficiencias
  efficiencySr89: number;
  efficiencySr89Components?: any;
  efficiencySr90: number;
  efficiencySr90Components?: any;
  efficiencyY90: number;
  efficiencyY90Components?: any;
  
  // Datos de contaje
  cpmFondo: number;
  countingTime: number;
  errorCpmContaje1: number;
  errorCpmContaje1Components?: any;
  errorCpmContaje2: number;
  errorCpmContaje2Components?: any;
  
  // Constantes de desintegración
  cteDisintegrationSr89: number;
  cteDisintegrationSr90: number;
  cteDisintegrationY90: number;
  
  // Datos del cálculo
  t1: number; // Primer contaje
  t2: number; // Segundo contaje
  calculationPrecipitationFinalTime: number;
  calculationSamplingTimeMin: number;
  
  // CPM
  cpm1Contaje: number;
  cpm2Contaje: number;
  
  // Factores intermedios
  factorB: number; // exp((-cte_y90 * t2))
  factorBComponents?: any;
  factorC: number; // exp((-cte_y90 * t1))
  factorCComponents?: any;
  factorD: number; // exp((-cte_sr89 * t1))
  factorDComponents?: any;
  factorE: number; // exp((-cte_sr89 * t2))
  factorEComponents?: any;
  factorM: number; // cte_y90 / (cte_y90 - cte_sr90)
  factorMComponents?: any;
  factorN: number; // eff_y90 / eff_sr90
  factorNComponents?: any;
  
  // CPM neto
  cpmNeto1: number; // cpm1_contaje - cpm_fondo
  cpmNeto2: number; // cpm2_contaje - cpm_fondo
  
  // Términos de la ecuación
  term1Numerator: number; // cpmNeto1 * (1 + m * n * (1 - b))
  term2Numerator: number; // cpmNeto2 * (1 + m * n * (1 - c))
  numeratorCpm0Sr89: number; // term1 - term2
  
  term1Denominator: number; // d * (1 + m * n * (1 - b))
  term2Denominator: number; // e * (1 + m * n * (1 - c))
  denominatorCpm0Sr89: number; // term1 - term2
  
  cpm0Sr89: number;
  errorCpm0Sr89: number;
  
  // Cálculo de actividad
  sampleQuantity: number;
  expValueActivity: number; // e^(cte_sr89 * final_time)
  expValue2Activity: number; // e^(-cte_sr89 * sampling_time)
  efficiencySr89Value: number;
  resultToCalculation: number;
  
  dividendoActivity: number;
  divisorActivity: number;
  
  // Resultados finales
  activity: number;
  uncertainty: number;
  lid: number;
  
  // Datos adicionales para LID
  sqrtCpmFondoTime: number;
  lidNumerator: number;
  lidDenominator: number;
  
  // Datos adicionales para incertidumbre
  uncertaintyNumerator: number;
  uncertaintyDenominator: number;
}

const Sr89DebugPage: React.FC = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState<Sr89DebugResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [calculationId, setCalculationId] = useState<string>('');
  
  const { control, handleSubmit, formState: { errors } } = useForm<Sr89DebugFormData>({
    defaultValues: {
      calculationId: '',
    },
  });

  const onSubmit = async (data: Sr89DebugFormData) => {
    setLoading(true);
    setError(null);
    setResults(null);
    setCalculationId(data.calculationId);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`http://localhost:8000/api/debug/sr89/${data.calculationId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setResults(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al obtener los datos de depuración');
    } finally {
      setLoading(false);
    }
  };

  const renderSection = (title: string, data: Array<{ label: string; value: any; formula?: string; components?: any }>) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom color="primary">
          {title}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {data.map((item, index) => (
            <Grid item xs={12} key={index}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                  {item.label}
                </Typography>
                {item.formula && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', mb: 0.5 }}>
                    {item.formula}
                  </Typography>
                )}
                <Typography variant="h6" fontWeight="bold" color="primary" sx={{ my: 1 }}>
                  {typeof item.value === 'number' ? item.value.toExponential(6) : item.value}
                </Typography>
                
                {/* Mostrar componentes si existen */}
                {item.components && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                      📊 Componentes del cálculo:
                    </Typography>
                    <Grid container spacing={1}>
                      {Object.entries(item.components).map(([key, value]: [string, any]) => (
                        <Grid item xs={12} sm={6} md={4} key={key}>
                          <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              {key.replace(/_/g, ' ')}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem', fontFamily: 'monospace' }}>
                              {typeof value === 'number' ? value.toExponential(4) : value}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4">
            Depuración de Cálculos Sr-89 (Método Currie)
          </Typography>
          <Button
            variant="outlined"
            startIcon={<CompareArrowsIcon />}
            onClick={() => navigate('/admin/debug/comparar')}
          >
            Comparar Cálculos
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          Esta herramienta permite visualizar todos los componentes intermedios del cálculo de actividad, 
          incertidumbre y LID para el análisis de Sr-89.
        </Typography>
        
        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Controller
                name="calculationId"
                control={control}
                rules={{ required: 'El ID del cálculo es requerido' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="ID del Cálculo (Calculation ID)"
                    variant="outlined"
                    error={!!errors.calculationId}
                    helperText={errors.calculationId?.message}
                    placeholder="Ingrese el ID del cálculo a depurar"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Depurar'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {results && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <ExportDebugData results={results} calculationId={calculationId} />
          </Box>
          
          {renderSection('1. Eficiencias', [
            { 
              label: 'Eficiencia Sr-89', 
              value: results.efficiencySr89, 
              formula: 'x4*sqp⁴ + x3*sqp³ + x2*sqp² + x*sqp + a',
              components: results.efficiencySr89Components
            },
            { 
              label: 'Eficiencia Sr-90', 
              value: results.efficiencySr90, 
              formula: 'x4*sqp⁴ + x3*sqp³ + x2*sqp² + x*sqp + a',
              components: results.efficiencySr90Components
            },
            { 
              label: 'Eficiencia Y-90', 
              value: results.efficiencyY90, 
              formula: 'x4*sqp⁴ + x3*sqp³ + x2*sqp² + x*sqp + a',
              components: results.efficiencyY90Components
            },
          ])}

          {renderSection('2. Datos de Contaje y Fondo', [
            { label: 'CPM Fondo', value: results.cpmFondo },
            { label: 'Tiempo Total Contaje Fondos (min)', value: results.countingTime },
            { 
              label: 'Error CPM 1er Contaje', 
              value: results.errorCpmContaje1, 
              formula: 'cpm1/time1 + cpmFondo/timeFondo',
              components: results.errorCpmContaje1Components
            },
            { 
              label: 'Error CPM 2do Contaje', 
              value: results.errorCpmContaje2, 
              formula: 'cpm2/time2 + cpmFondo/timeFondo',
              components: results.errorCpmContaje2Components
            },
          ])}

          {renderSection('3. Constantes de Desintegración', [
            { label: 'λ Sr-89 (Cte. desintegración)', value: results.cteDisintegrationSr89 },
            { label: 'λ Sr-90 (Cte. desintegración)', value: results.cteDisintegrationSr90 },
            { label: 'λ Y-90 (Cte. desintegración)', value: results.cteDisintegrationY90 },
          ])}

          {renderSection('4. Tiempos de Precipitación y Muestreo', [
            { label: 't1 - Primer Contaje (min)', value: results.t1 },
            { label: 't2 - Segundo Contaje (min)', value: results.t2 },
            { label: 'Tiempo Final Precipitación (min)', value: results.calculationPrecipitationFinalTime },
            { label: 'Tiempo de Muestreo (min)', value: results.calculationSamplingTimeMin },
          ])}

          {renderSection('5. CPM de Contajes', [
            { label: 'CPM 1er Contaje', value: results.cpm1Contaje },
            { label: 'CPM 2do Contaje', value: results.cpm2Contaje },
            { label: 'CPM Neto 1', value: results.cpmNeto1, formula: 'cpm1_contaje - cpm_fondo' },
            { label: 'CPM Neto 2', value: results.cpmNeto2, formula: 'cpm2_contaje - cpm_fondo' },
          ])}

          {renderSection('6. Factores Exponenciales y Auxiliares', [
            { label: 'Factor b', value: results.factorB, formula: 'exp(-λ_y90 * t2)', components: results.factorBComponents },
            { label: 'Factor c', value: results.factorC, formula: 'exp(-λ_y90 * t1)', components: results.factorCComponents },
            { label: 'Factor d', value: results.factorD, formula: 'exp(-λ_sr89 * t1)', components: results.factorDComponents },
            { label: 'Factor e', value: results.factorE, formula: 'exp(-λ_sr89 * t2)', components: results.factorEComponents },
            { label: 'Factor m', value: results.factorM, formula: 'λ_y90 / (λ_y90 - λ_sr90)', components: results.factorMComponents },
            { label: 'Factor n', value: results.factorN, formula: 'eff_y90 / eff_sr90', components: results.factorNComponents },
          ])}

          {renderSection('7. Cálculo de CPM₀ Sr-89', [
            { label: 'Término 1 Numerador', value: results.term1Numerator, formula: 'cpmNeto1 * (1 + m*n*(1-b))' },
            { label: 'Término 2 Numerador', value: results.term2Numerator, formula: 'cpmNeto2 * (1 + m*n*(1-c))' },
            { label: 'Numerador CPM₀ Sr-89', value: results.numeratorCpm0Sr89, formula: 'term1 - term2' },
            { label: 'Término 1 Denominador', value: results.term1Denominator, formula: 'd * (1 + m*n*(1-b))' },
            { label: 'Término 2 Denominador', value: results.term2Denominator, formula: 'e * (1 + m*n*(1-c))' },
            { label: 'Denominador CPM₀ Sr-89', value: results.denominatorCpm0Sr89, formula: 'term1 - term2' },
            { label: 'CPM₀ Sr-89', value: results.cpm0Sr89, formula: 'numerador / denominador' },
            { label: 'Error CPM₀ Sr-89', value: results.errorCpm0Sr89, formula: 'sqrt((errCpm1*(1+m*n*(1-b)))² + (errCpm2*(1+m*n*(1-c)))²) / denominador' },
          ])}

          {renderSection('8. Componentes del Cálculo de Actividad', [
            { label: 'Cantidad de Muestra', value: results.sampleQuantity },
            { label: 'exp(λ_sr89 * t_final)', value: results.expValueActivity, formula: 'e^(λ_sr89 * tiempo_final)' },
            { label: 'exp(-λ_sr89 * t_muestreo)', value: results.expValue2Activity, formula: 'e^(-λ_sr89 * tiempo_muestreo)' },
            { label: 'Eficiencia Sr-89', value: results.efficiencySr89Value },
            { label: 'Resultado para Cálculo (AA)', value: results.resultToCalculation },
            { label: 'Dividendo Actividad', value: results.dividendoActivity, formula: 'cpm0_sr89 * expValue * λ_sr89 * t_muestreo' },
            { label: 'Divisor Actividad', value: results.divisorActivity, formula: '60 * eff_sr89 * cantidad_muestra * resultado_AA * (1 - expValue2)' },
          ])}

          {renderSection('9. Componentes del Cálculo de LID', [
            { label: 'sqrt(cpm_fondo * tiempo_total)', value: results.sqrtCpmFondoTime },
            { label: 'Numerador LID', value: results.lidNumerator, formula: '4.66 * sqrt(...) / tiempo_total * expValue * λ * t_muestreo' },
            { label: 'Denominador LID', value: results.lidDenominator, formula: '60 * eff * cantidad * resultado_AA * (1 - expValue2)' },
          ])}

          {renderSection('10. Componentes del Cálculo de Incertidumbre', [
            { label: 'Numerador Incertidumbre', value: results.uncertaintyNumerator, formula: '2 * error_cpm0_sr89 * expValue * λ * t_muestreo' },
            { label: 'Denominador Incertidumbre', value: results.uncertaintyDenominator, formula: '60 * eff * cantidad * resultado_AA * (1 - expValue2)' },
          ])}

          {renderSection('11. RESULTADOS FINALES', [
            { label: '🎯 ACTIVIDAD (Bq/m³)', value: results.activity },
            { label: '📊 INCERTIDUMBRE', value: results.uncertainty },
            { label: '🔍 LID (Límite de Detección)', value: results.lid },
          ])}
        </Box>
      )}
    </Box>
  );
};

export default Sr89DebugPage;
